import {
  DEFAULT_GRID_ROWS,
  REPORT_PAGE_PADDING_BOTTOM_ROWS,
} from '../../PageConfigProvider/constants';
import type {
  Position,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import { MIN_ITEM_HEIGHT_CELLS, MIN_ITEM_WIDTH_CELLS } from '../constants';
import {
  buildOccupancyMatrix,
  isAreaFree,
  occupyArea,
} from './grid-layout-utils';
import { reportPageY } from './report-page-boundaries';

const INITIAL_REPORT_GHOST_BOTTOM_COUNT = 2;

export type GhostPlaceholder = Pick<PositionedItem, 'id' | 'position'>;

type GridDimensions = {
  rows: number;
  columns: number;
};

type Axis = 'horizontal' | 'vertical';

const dimensionProps = {
  horizontal: {
    delta: 'x' as const,
    size: 'width' as const,
    minSize: MIN_ITEM_WIDTH_CELLS,
  },
  vertical: {
    delta: 'y' as const,
    size: 'height' as const,
    minSize: MIN_ITEM_HEIGHT_CELLS,
  },
} as const;

function rowHasRealItem(rowY: number, realMatrix: boolean[][]): boolean {
  const row = realMatrix[rowY];
  if (row === undefined) return false;
  return row.some(Boolean);
}

export function calculateReportGhosts({
  items,
  grid,
}: {
  items: PositionedItem[];
  grid: GridDimensions;
}): GhostPlaceholder[] {
  const realItemsMatrix = buildOccupancyMatrix(grid, items);
  const matrix = realItemsMatrix.map((row) => [...row]);
  const ghosts: GhostPlaceholder[] = [];
  let ghostId = 0;

  const numPages = Math.ceil(grid.rows / DEFAULT_GRID_ROWS);

  for (let p = 0; p < numPages; p++) {
    const pageStart = p * DEFAULT_GRID_ROWS;
    const pageEnd = Math.min(pageStart + DEFAULT_GRID_ROWS, grid.rows);
    const innerStart = Math.min(pageEnd, reportPageY(p));
    const innerEnd = Math.max(
      innerStart,
      pageEnd - Math.max(0, REPORT_PAGE_PADDING_BOTTOM_ROWS)
    );

    const pageHasItems = items.some(
      (it) =>
        it.position.y < pageEnd &&
        it.position.y + it.position.height > pageStart
    );
    if (!pageHasItems) {
      const initial = createInitialReportPageGhosts(grid, innerStart, innerEnd);
      for (const g of initial) {
        if (isAreaFree(g.position, grid, matrix)) {
          ghosts.push({ id: `ghost-${ghostId++}`, position: g.position });
          occupyArea(g.position, matrix);
        }
      }
      continue;
    }

    ghostId = addFullWidthGhostsForEmptyBands(
      ghosts,
      grid,
      matrix,
      realItemsMatrix,
      innerStart,
      innerEnd,
      ghostId
    );

    const pageItems = items
      .filter(
        (it) =>
          it.position.y >= innerStart &&
          it.position.y + it.position.height <= innerEnd
      )
      .sort(
        (a, b) =>
          b.position.width * b.position.height -
          a.position.width * a.position.height
      );

    for (const { position: template } of pageItems) {
      const seedItems: Position[] = [template];

      const horizontalClones = replicateInDirection(
        template,
        'horizontal',
        grid,
        matrix,
        innerStart,
        innerEnd
      );
      for (const pos of horizontalClones) {
        ghosts.push({ id: `ghost-${ghostId++}`, position: pos });
        seedItems.push(pos);
      }

      for (const seed of seedItems) {
        const verticalClones = replicateInDirection(
          seed,
          'vertical',
          grid,
          matrix,
          innerStart,
          innerEnd
        );
        for (const pos of verticalClones) {
          ghosts.push({ id: `ghost-${ghostId++}`, position: pos });
        }
      }
    }

    expandGhostsToFillWithinPage(ghosts, grid, matrix, innerStart, innerEnd);
    addBottomStripGhosts(ghosts, grid, matrix, innerStart, innerEnd);
  }

  return ghosts;
}

function addFullWidthGhostsForEmptyBands(
  ghosts: GhostPlaceholder[],
  grid: GridDimensions,
  matrix: boolean[][],
  realItemsMatrix: boolean[][],
  innerStart: number,
  innerEnd: number,
  nextGhostIndex: number
): number {
  let ghostIndex = nextGhostIndex;
  let y = innerStart;
  while (y < innerEnd) {
    if (rowHasRealItem(y, realItemsMatrix)) {
      y += 1;
      continue;
    }
    const bandStart = y;
    let bandEnd = y;
    while (bandEnd < innerEnd && !rowHasRealItem(bandEnd, realItemsMatrix)) {
      bandEnd += 1;
    }
    const bandHeight = bandEnd - bandStart;
    if (bandHeight >= MIN_ITEM_HEIGHT_CELLS) {
      const pos: Position = {
        x: 0,
        y: bandStart,
        width: grid.columns,
        height: bandHeight,
      };
      if (isAreaFree(pos, grid, matrix)) {
        ghosts.push({ id: `ghost-${ghostIndex}`, position: pos });
        ghostIndex += 1;
        occupyArea(pos, matrix);
      }
    }
    y = bandEnd;
  }
  return ghostIndex;
}

function createInitialReportPageGhosts(
  grid: GridDimensions,
  innerStart: number,
  innerEnd: number
): GhostPlaceholder[] {
  const usableHeight = innerEnd - innerStart;
  if (usableHeight < MIN_ITEM_HEIGHT_CELLS) return [];

  const topHalfRows = Math.floor(usableHeight / 2);
  const bottomHalfRows = usableHeight - topHalfRows;
  const ghosts: GhostPlaceholder[] = [];

  const topGhost: GhostPlaceholder = {
    id: 'ghost-initial-top',
    position: {
      x: 0,
      y: innerStart,
      width: grid.columns,
      height: topHalfRows,
    },
  };
  ghosts.push(topGhost);

  const bottomRowHeight = Math.floor(
    bottomHalfRows / INITIAL_REPORT_GHOST_BOTTOM_COUNT
  );
  const remainder =
    bottomHalfRows - bottomRowHeight * INITIAL_REPORT_GHOST_BOTTOM_COUNT;
  let y = innerStart + topHalfRows;
  for (let i = 0; i < INITIAL_REPORT_GHOST_BOTTOM_COUNT; i += 1) {
    const h = i === 0 ? bottomRowHeight + remainder : bottomRowHeight;
    ghosts.push({
      id: `ghost-initial-bottom-${i}`,
      position: { x: 0, y, width: grid.columns, height: h },
    });
    y += h;
  }
  return ghosts.filter((g) => g.position.height >= MIN_ITEM_HEIGHT_CELLS);
}

function replicateInDirection(
  base: Position,
  dir: Axis,
  grid: GridDimensions,
  matrix: boolean[][],
  pageStart: number,
  pageEnd: number
): Position[] {
  const results: Position[] = [];
  const { delta, size, minSize } = dimensionProps[dir];
  const limit = dir === 'horizontal' ? grid.columns : pageEnd;
  const directions = [1, -1] as const;

  for (const direction of directions) {
    let nextPos = base[delta] + direction * base[size];
    let lastPlaced: Position | null = null;

    while (
      (direction > 0 &&
        (dir === 'horizontal'
          ? nextPos + base[size] <= limit
          : nextPos + base[size] <= pageEnd)) ||
      (direction < 0 &&
        (dir === 'horizontal' ? nextPos >= 0 : nextPos >= pageStart))
    ) {
      const pos: Position =
        dir === 'horizontal'
          ? { ...base, [delta]: nextPos }
          : { ...base, [delta]: nextPos };
      if (
        pos.y < pageStart ||
        pos.y + pos.height > pageEnd ||
        !isAreaFree(pos, grid, matrix)
      ) {
        break;
      }
      occupyArea(pos, matrix);
      results.push(pos);
      lastPlaced = pos;
      nextPos += direction * base[size];
    }

    const spanStart =
      direction > 0 ? nextPos : (lastPlaced?.[delta] ?? base[delta]);
    const maxLength = computeFreeSpanWithinPage(
      base,
      dir,
      direction,
      spanStart,
      grid,
      matrix,
      pageStart,
      pageEnd
    );

    if (maxLength >= minSize) {
      const pos: Position = { ...base, [size]: maxLength };
      pos[delta] = direction > 0 ? spanStart : spanStart - maxLength;
      if (
        pos.y >= pageStart &&
        pos.y + pos.height <= pageEnd &&
        isAreaFree(pos, grid, matrix)
      ) {
        occupyArea(pos, matrix);
        results.push(pos);
      }
    } else if (lastPlaced && maxLength > 0) {
      if (direction > 0) {
        lastPlaced[size] += maxLength;
      } else {
        lastPlaced[delta] -= maxLength;
        lastPlaced[size] += maxLength;
      }
    }
  }

  return results;
}

function computeFreeSpanWithinPage(
  base: Position,
  dir: Axis,
  direction: number,
  start: number,
  grid: GridDimensions,
  matrix: boolean[][],
  pageStart: number,
  pageEnd: number
): number {
  const isHorizontal = dir === 'horizontal';
  const gridLimit = isHorizontal ? grid.columns : pageEnd;

  let length = 0;
  let major = direction > 0 ? start : start - 1;

  while (major >= (isHorizontal ? 0 : pageStart) && major < gridLimit) {
    const slice: Position = {
      x: isHorizontal ? major : base.x,
      y: isHorizontal ? base.y : major,
      width: isHorizontal ? 1 : base.width,
      height: isHorizontal ? base.height : 1,
    };

    if (slice.y < pageStart || slice.y + slice.height > pageEnd) return length;
    if (!isAreaFree(slice, grid, matrix)) return length;
    length++;
    major += direction;
  }

  return length;
}

function expandGhostsToFillWithinPage(
  ghosts: GhostPlaceholder[],
  grid: GridDimensions,
  matrix: boolean[][],
  pageStart: number,
  pageEnd: number
): void {
  // Expand in-place: sort by right edge
  ghosts.sort(
    (a, b) =>
      a.position.x + a.position.width - (b.position.x + b.position.width)
  );

  for (const ghost of ghosts) {
    const p = ghost.position;
    // Only expand ghosts inside this page
    if (p.y < pageStart || p.y + p.height > pageEnd) continue;

    (['horizontal', 'vertical'] as const).forEach((axis) => {
      const { delta, size } = dimensionProps[axis];

      const backwardSpan = computeFreeSpanWithinPage(
        p,
        axis,
        -1,
        p[delta],
        grid,
        matrix,
        pageStart,
        pageEnd
      );
      if (backwardSpan > 0) {
        p[delta] -= backwardSpan;
        p[size] += backwardSpan;
        occupyArea({ ...p, [size]: backwardSpan, [delta]: p[delta] }, matrix);
      }

      const forwardSpan = computeFreeSpanWithinPage(
        p,
        axis,
        1,
        p[delta] + p[size],
        grid,
        matrix,
        pageStart,
        pageEnd
      );
      if (forwardSpan > 0) {
        const oldSize = p[size];
        p[size] += forwardSpan;
        occupyArea(
          { ...p, [delta]: p[delta] + oldSize, [size]: forwardSpan },
          matrix
        );
      }
    });
  }
}

function addBottomStripGhosts(
  ghosts: GhostPlaceholder[],
  grid: GridDimensions,
  matrix: boolean[][],
  innerStart: number,
  innerEnd: number
): void {
  const stripHeight = MIN_ITEM_HEIGHT_CELLS;
  const y = innerEnd - stripHeight;
  if (y < innerStart) return;

  const fullStripPos: Position = {
    x: 0,
    y,
    width: grid.columns,
    height: stripHeight,
  };
  if (isAreaFree(fullStripPos, grid, matrix)) {
    ghosts.push({
      id: `ghost-bottom-${ghosts.length}`,
      position: fullStripPos,
    });
    occupyArea(fullStripPos, matrix);
    return;
  }

  const blockWidth = Math.max(
    MIN_ITEM_WIDTH_CELLS,
    Math.floor(grid.columns / 3)
  );
  let ghostId = ghosts.length;
  for (let x = 0; x <= grid.columns - blockWidth; x += blockWidth) {
    const pos: Position = { x, y, width: blockWidth, height: stripHeight };
    if (!isAreaFree(pos, grid, matrix)) continue;
    ghosts.push({ id: `ghost-bottom-${ghostId++}`, position: pos });
    occupyArea(pos, matrix);
  }
}
